import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Swords, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getOpsColor(ops) {
  const v = parseFloat(ops) || 0;
  if (v >= .900) return "text-green-400";
  if (v >= .750) return "text-green-300";
  if (v >= .650) return "text-white";
  if (v >= .550) return "text-red-300";
  return "text-red-400";
}

export default function CareerVsTeamTab() {
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [sortBy, setSortBy] = useState("ops");
  const [sortDir, setSortDir] = useState("desc");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const res = await axios.get(`${API}/teams/mlb`);
        setTeams(res.data.teams || []);
      } catch (e) {
        console.error("Error fetching teams:", e);
      } finally {
        setTeamsLoading(false);
      }
    };
    fetchTeams();
  }, []);

  useEffect(() => {
    if (!selectedTeam) return;
    const fetchStats = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/stats/career-vs-team/${selectedTeam.id}`);
        setPlayers(res.data.players || []);
      } catch (e) {
        console.error("Error fetching career vs team:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [selectedTeam]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir(sortDir === "desc" ? "asc" : "desc");
    } else {
      setSortBy(key);
      setSortDir(key === "strikeouts" ? "asc" : "desc");
    }
  };

  const rateStats = new Set(["avg", "obp", "slg", "ops"]);
  const sorted = [...players].sort((a, b) => {
    const av = rateStats.has(sortBy) ? parseFloat(a[sortBy] || "0") : (a[sortBy] ?? 0);
    const bv = rateStats.has(sortBy) ? parseFloat(b[sortBy] || "0") : (b[sortBy] ?? 0);
    return sortDir === "desc" ? bv - av : av - bv;
  });

  const SortHeader = ({ field, label, className = "" }) => (
    <th
      className={`text-center py-3 px-2 font-medium cursor-pointer hover:text-astros-orange transition-colors ${className}`}
      onClick={() => handleSort(field)}
    >
      {label} {sortBy === field && (sortDir === "desc" ? "▼" : "▲")}
    </th>
  );

  // Group teams by division for the selector
  const divisions = {};
  teams.forEach(t => {
    const div = t.division || "Other";
    if (!divisions[div]) divisions[div] = [];
    divisions[div].push(t);
  });

  return (
    <div data-testid="career-vs-tab">
      <div className="flex items-center gap-3 mb-2">
        <Swords className="w-6 h-6 text-astros-orange" />
        <h2 className="font-bebas text-2xl sm:text-3xl tracking-tight text-white">CAREER VS TEAM</h2>
      </div>
      <p className="text-astros-muted text-sm mb-6">
        Career hitting stats for Astros players against each MLB team. Select a team to see who hits best and worst.
      </p>

      {/* Team selector */}
      {teamsLoading ? (
        <div className="flex items-center gap-2 mb-6">
          <div className="w-5 h-5 border-2 border-astros-orange border-t-transparent rounded-full animate-spin"></div>
          <span className="text-astros-muted text-sm">Loading teams...</span>
        </div>
      ) : (
        <div className="mb-6">
          <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-10 gap-2">
            {teams.map((team) => (
              <button
                key={team.id}
                onClick={() => { setSelectedTeam(team); setSortBy("ops"); setSortDir("desc"); }}
                className={`px-2 py-2 text-xs font-bebas tracking-wide transition-all border ${
                  selectedTeam?.id === team.id
                    ? "bg-astros-orange/20 border-astros-orange text-astros-orange"
                    : "border-astros-border text-astros-muted hover:text-white hover:border-white/30"
                }`}
                data-testid={`team-btn-${team.abbreviation}`}
              >
                {team.abbreviation}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      {!selectedTeam && !loading && (
        <div className="text-center py-16">
          <ChevronDown className="w-8 h-8 text-astros-muted mx-auto mb-3 animate-bounce" />
          <p className="text-astros-muted text-lg font-bebas tracking-wide">SELECT A TEAM ABOVE</p>
          <p className="text-astros-muted/60 text-sm mt-1">to see career hitting splits</p>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-astros-orange border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {selectedTeam && !loading && (
        <>
          <h3 className="font-bebas text-xl text-white mb-4 tracking-wide">
            CAREER VS <span className="text-astros-orange">{selectedTeam.name.toUpperCase()}</span>
          </h3>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="career-vs-table">
              <thead>
                <tr className="border-b border-astros-border text-astros-muted text-xs uppercase tracking-wider">
                  <th className="text-left py-3 pr-2 w-8"></th>
                  <th className="text-left py-3 pr-4 font-medium">Player</th>
                  <SortHeader field="games" label="G" />
                  <SortHeader field="at_bats" label="AB" />
                  <SortHeader field="hits" label="H" />
                  <SortHeader field="home_runs" label="HR" />
                  <SortHeader field="rbi" label="RBI" />
                  <SortHeader field="walks" label="BB" />
                  <SortHeader field="strikeouts" label="K" />
                  <SortHeader field="avg" label="AVG" />
                  <SortHeader field="obp" label="OBP" className="hidden sm:table-cell" />
                  <SortHeader field="slg" label="SLG" className="hidden sm:table-cell" />
                  <SortHeader field="ops" label="OPS" />
                </tr>
              </thead>
              <tbody>
                {sorted.map((player, index) => (
                  <motion.tr
                    key={player.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.02 }}
                    className="border-b border-astros-border/30 hover:bg-astros-border/20 cursor-pointer transition-colors"
                    onClick={() => navigate(`/player/${player.id}`)}
                    data-testid={`career-vs-player-${player.id}`}
                  >
                    <td className="py-3 pr-2">
                      <img
                        src={player.photo_url}
                        alt={player.name}
                        className="w-8 h-8 object-cover bg-astros-navy"
                        onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1770228783704-32823e759c03?w=32&h=32&fit=crop"; }}
                      />
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex flex-col">
                        <span className="text-white font-semibold text-sm">{player.name}</span>
                        <div className="flex items-center gap-2 text-xs text-astros-muted">
                          <span className="text-astros-orange font-bold">#{player.jersey_number || "00"}</span>
                          <span>{player.position}</span>
                        </div>
                      </div>
                    </td>
                    <td className="text-center py-3 px-2 text-white">{player.games}</td>
                    <td className="text-center py-3 px-2 text-white">{player.at_bats}</td>
                    <td className="text-center py-3 px-2 text-white font-semibold">{player.hits}</td>
                    <td className="text-center py-3 px-2 text-white font-semibold">{player.home_runs}</td>
                    <td className="text-center py-3 px-2 text-white font-semibold">{player.rbi}</td>
                    <td className="text-center py-3 px-2 text-white">{player.walks}</td>
                    <td className="text-center py-3 px-2 text-white">{player.strikeouts}</td>
                    <td className="text-center py-3 px-2 text-white font-semibold">{player.avg}</td>
                    <td className="text-center py-3 px-2 text-white hidden sm:table-cell">{player.obp}</td>
                    <td className="text-center py-3 px-2 text-white hidden sm:table-cell">{player.slg}</td>
                    <td className={`text-center py-3 px-2 font-bold ${getOpsColor(player.ops)}`}>
                      {player.ops}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {players.length === 0 && (
            <p className="text-astros-muted text-center py-8">No career data available vs this team</p>
          )}
        </>
      )}
    </div>
  );
}
